import { useState, useEffect, useRef } from 'react';
import { User } from '@/types';
import { supabase } from '@/integrations/supabase/client';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isInitialized = useRef(false);
  const loadingProfile = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const loadUserProfile = async (userId: string) => {
      // Prevent concurrent profile loading
      if (loadingProfile.current) {
        return;
      }
      loadingProfile.current = true;

      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (profileError) {
          console.error('Error fetching profile:', profileError);
          // Profile might not exist yet for new users - wait and retry once
          if (profileError.code === 'PGRST116') {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const { data: retryProfile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', userId)
              .single();
            
            if (retryProfile && isMounted) {
              await setUserFromProfile(retryProfile, userId);
            }
          }
          return;
        }

        if (profile && isMounted) {
          await setUserFromProfile(profile, userId);
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        loadingProfile.current = false;
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const setUserFromProfile = async (profile: any, userId: string) => {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        
        // Get user's role from the secure user_roles table
        const { data: roleData, error: roleError } = await supabase
          .rpc('get_user_role', { _user_id: userId });
        
        if (roleError) {
          console.error('Error fetching user role:', roleError);
        }
        
        setUser({
          id: userId,
          email: authUser?.email || '',
          name: profile.full_name || profile.username || '',
          role: roleData || null, // Handle null role gracefully
          phone: profile.phone,
          whatsappNumber: profile.whatsapp_number,
          avatar: profile.avatar_url,
          createdAt: new Date(profile.created_at),
          updatedAt: new Date(profile.updated_at),
        });
      } catch (error) {
        console.error('Error setting user from profile:', error);
      }
    };

    // INITIAL load (controls isLoading) - runs once
    const initializeAuth = async () => {
      if (isInitialized.current) return;
      isInitialized.current = true;

      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          if (isMounted) setLoading(false);
          return;
        }

        if (session?.user) {
          await loadUserProfile(session.user.id);
        } else {
          if (isMounted) setLoading(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (isMounted) setLoading(false);
      }
    };

    // Listener for ONGOING auth changes (does NOT control isLoading on subsequent changes)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;

        // Only handle auth changes after initialization
        if (!isInitialized.current) return;

        if (event === 'SIGNED_OUT') {
          setUser(null);
          loadingProfile.current = false;
          return;
        }

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          if (session?.user) {
            // Small delay to ensure profile trigger has executed for new users
            if (event === 'SIGNED_IN') {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
            loadUserProfile(session.user.id);
          }
        }
      }
    );

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  return { user, loading, logout };
}
